import { useMemo, useState } from 'react'

import { getCategories } from '../../../../utilities/api'

import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Offcanvas from 'react-bootstrap/Offcanvas'
import Stack from 'react-bootstrap/Stack'

import { UploadImage } from '../../../UploadImage'
import {
  buildBulkSettingsPatch,
  bulkSettingsFieldLabels,
  validateBulkSettingsPatch,
} from './bulkSettingsClient'

const EMPTY_FORM = {
  includeIsVisible: false,
  isVisible: true,
  includeEventVisibility: false,
  eventVisibility: '',
  includeGeneralOnsale: false,
  generalOnsale: '',
  includeFacilityFees: false,
  facilityFees: '',
  includeFeeFlags: false,
  includeTaxes: true,
  includeFees: true,
  includeServiceFees: true,
  includeTicketLimit: false,
  ticketLimit: false,
  globalTicketLimit: '',
  includeResale: false,
  resaleEnableMinimum: false,
  resaleMinimumPercentage: '',
  includeEnableResale: false,
  enableResale: false,
  includeWaitingRoom: false,
  waitingRoomEnabled: false,
  waitingRoomMaxConcurrent: '75',
  waitingRoomAdmissionTtlMinutes: '15',
  includeImage: false,
  image: null,
  includeDescription: false,
  description: '',
  includePromotionalDescription: false,
  promotionalDescription: '',
  includeInvoiceEmailNote: false,
  invoiceEmailNote: '',
  includeCategory: false,
  category: '',
  includeSubcategories: false,
  sub_categories: [],
}

function SettingBlock({ id, label, checked, onChange, children }) {
  return (
    <div className="mb-4">
      <Form.Check
        type="checkbox"
        id={id}
        label={label}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      {checked ? <div className="mt-2 ps-4">{children}</div> : null}
    </div>
  )
}

export default function BulkEventSettingsPanel({
  show,
  eventCount,
  processing,
  waitingRoomFeatureEnabled,
  onClose,
  onApply,
}) {
  const [form, setForm] = useState(EMPTY_FORM)
  const [categories, setCategories] = useState([])
  const [loadedCategories, setLoadedCategories] = useState(false)
  const [error, setError] = useState('')

  const patch = useMemo(() => buildBulkSettingsPatch(form), [form])

  const labels = bulkSettingsFieldLabels(patch)
  const missingDateMessage = [
    form.includeEventVisibility && !form.eventVisibility
      ? 'Scheduled visibility needs a date.'
      : null,
    form.includeGeneralOnsale && !form.generalOnsale
      ? 'General onsale needs a date.'
      : null,
  ]
    .filter(Boolean)
    .join(' ')

  const setField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }))
  }

  const selectedCategory = categories.find(
    (category) =>
      form.includeCategory && String(category.id) === String(form.category)
  )
  const availableSubCategories = selectedCategory?.subCategories || []

  const loadCategories = () => {
    if (loadedCategories) return
    setLoadedCategories(true)
    getCategories()
      .then((res) => setCategories(res?.data || []))
      .catch(() => setCategories([]))
  }

  const handleApply = () => {
    if (missingDateMessage) return

    const checking = { ...patch }
    const imagePending = typeof checking.image === 'string'
    if (imagePending) delete checking.image

    if (Object.keys(checking).length === 0 && !imagePending) {
      setError('Choose at least one setting to update.')
      return
    }

    if (Object.keys(checking).length > 0) {
      const validation = validateBulkSettingsPatch(checking, {
        waitingRoomFeatureEnabled,
      })
      if (!validation.ok) {
        setError(validation.issues.map((issue) => issue.message).join(' '))
        return
      }
    }

    setError('')
    onApply(patch, labels)
  }

  return (
    <Offcanvas
      show={show}
      onHide={processing ? undefined : onClose}
      placement="end"
      animation={false}
    >
      <Offcanvas.Header closeButton={!processing}>
        <Offcanvas.Title as="h2" className="card-title card-title-lg">
          Bulk edit settings
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <p className="mb-3" data-testid="bulk-settings-count">
          This will update <strong>{eventCount}</strong>{' '}
          {eventCount === 1 ? 'event' : 'events'}. Each event is saved on its
          own. A problem with one event does not undo the others.
        </p>
        <p className="text-muted small mb-4">
          Only the settings you check are changed. Scheduled visibility and
          general onsale are the clock time you enter, saved in each event's
          venue timezone.
        </p>

        {missingDateMessage || error ? (
          <p className="text-danger mb-3" role="alert">
            {missingDateMessage || error}
          </p>
        ) : null}

        <SettingBlock
          id="bulk-is-visible"
          label="Fan visibility"
          checked={form.includeIsVisible}
          onChange={(checked) => setField('includeIsVisible', checked)}
        >
          <Form.Check
            type="switch"
            id="bulk-is-visible-value"
            label="Visible to fans"
            checked={form.isVisible}
            onChange={(event) => setField('isVisible', event.target.checked)}
          />
        </SettingBlock>

        <SettingBlock
          id="bulk-event-visibility"
          label="Scheduled visibility"
          checked={form.includeEventVisibility}
          onChange={(checked) => setField('includeEventVisibility', checked)}
        >
          <Form.Control
            type="datetime-local"
            aria-label="Scheduled visibility"
            value={form.eventVisibility}
            onChange={(event) =>
              setField('eventVisibility', event.target.value)
            }
          />
        </SettingBlock>

        <SettingBlock
          id="bulk-general-onsale"
          label="General onsale"
          checked={form.includeGeneralOnsale}
          onChange={(checked) => setField('includeGeneralOnsale', checked)}
        >
          <Form.Control
            type="datetime-local"
            aria-label="General onsale"
            value={form.generalOnsale}
            onChange={(event) => setField('generalOnsale', event.target.value)}
          />
        </SettingBlock>

        <SettingBlock
          id="bulk-facility-fees"
          label="Facility fees"
          checked={form.includeFacilityFees}
          onChange={(checked) => setField('includeFacilityFees', checked)}
        >
          <Form.Control
            type="number"
            min="0"
            step="0.01"
            aria-label="Facility fees"
            value={form.facilityFees}
            onChange={(event) => setField('facilityFees', event.target.value)}
          />
        </SettingBlock>

        <SettingBlock
          id="bulk-fee-flags"
          label="Fee inclusion"
          checked={form.includeFeeFlags}
          onChange={(checked) => setField('includeFeeFlags', checked)}
        >
          <Form.Check
            type="switch"
            id="bulk-include-taxes"
            label="Include taxes"
            checked={form.includeTaxes}
            onChange={(event) => setField('includeTaxes', event.target.checked)}
          />
          <Form.Check
            type="switch"
            id="bulk-include-fees"
            label="Include facility fees"
            checked={form.includeFees}
            onChange={(event) => setField('includeFees', event.target.checked)}
          />
          <Form.Check
            type="switch"
            id="bulk-include-service-fees"
            label="Include service fees"
            checked={form.includeServiceFees}
            onChange={(event) =>
              setField('includeServiceFees', event.target.checked)
            }
          />
        </SettingBlock>

        <SettingBlock
          id="bulk-ticket-limit"
          label="Ticket limits"
          checked={form.includeTicketLimit}
          onChange={(checked) => setField('includeTicketLimit', checked)}
        >
          <Form.Check
            type="switch"
            id="bulk-ticket-limit-enabled"
            label="Limit tickets per order"
            checked={form.ticketLimit}
            onChange={(event) => setField('ticketLimit', event.target.checked)}
          />
          {form.ticketLimit ? (
            <Form.Control
              className="mt-2"
              type="number"
              min="1"
              max="50"
              aria-label="Ticket limit"
              value={form.globalTicketLimit}
              onChange={(event) =>
                setField('globalTicketLimit', event.target.value)
              }
            />
          ) : null}
        </SettingBlock>

        <SettingBlock
          id="bulk-resale"
          label="Resale minimums"
          checked={form.includeResale}
          onChange={(checked) => setField('includeResale', checked)}
        >
          <Form.Check
            type="switch"
            id="bulk-resale-enabled"
            label="Require a minimum resale price"
            checked={form.resaleEnableMinimum}
            onChange={(event) =>
              setField('resaleEnableMinimum', event.target.checked)
            }
          />
          {form.resaleEnableMinimum ? (
            <Form.Control
              className="mt-2"
              type="number"
              min="30"
              aria-label="Resale minimum percentage"
              value={form.resaleMinimumPercentage}
              onChange={(event) =>
                setField('resaleMinimumPercentage', event.target.value)
              }
            />
          ) : null}
        </SettingBlock>

        <SettingBlock
          id="bulk-enable-resale"
          label="Allow resale"
          checked={form.includeEnableResale}
          onChange={(checked) => setField('includeEnableResale', checked)}
        >
          <Form.Check
            type="switch"
            id="bulk-enable-resale-value"
            label="Allow fans to resell tickets"
            checked={form.enableResale}
            onChange={(event) => setField('enableResale', event.target.checked)}
          />
        </SettingBlock>

        {waitingRoomFeatureEnabled ? (
          <SettingBlock
            id="bulk-waiting-room"
            label="Waiting room"
            checked={form.includeWaitingRoom}
            onChange={(checked) => setField('includeWaitingRoom', checked)}
          >
            <Form.Check
              type="switch"
              id="bulk-waiting-room-enabled"
              label="Enable waiting room"
              checked={form.waitingRoomEnabled}
              onChange={(event) =>
                setField('waitingRoomEnabled', event.target.checked)
              }
            />
            {form.waitingRoomEnabled ? (
              <Stack gap={3} className="mt-3">
                <Form.Group controlId="bulk-waiting-room-capacity">
                  <Form.Label>Max concurrent shoppers</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={form.waitingRoomMaxConcurrent}
                    onChange={(event) =>
                      setField('waitingRoomMaxConcurrent', event.target.value)
                    }
                  />
                </Form.Group>
                <Form.Group controlId="bulk-waiting-room-admission">
                  <Form.Label>Admission session length (minutes)</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={form.waitingRoomAdmissionTtlMinutes}
                    onChange={(event) =>
                      setField(
                        'waitingRoomAdmissionTtlMinutes',
                        event.target.value
                      )
                    }
                  />
                </Form.Group>
              </Stack>
            ) : null}
          </SettingBlock>
        ) : null}

        <SettingBlock
          id="bulk-image"
          label="Image"
          checked={form.includeImage}
          onChange={(checked) => setField('includeImage', checked)}
        >
          <UploadImage
            selectedImage={form.image}
            setSelectedImage={(image) => setField('image', image)}
            skipCrop
          />
        </SettingBlock>

        <SettingBlock
          id="bulk-description"
          label="Description"
          checked={form.includeDescription}
          onChange={(checked) => setField('includeDescription', checked)}
        >
          <Form.Control
            as="textarea"
            rows={4}
            aria-label="Description"
            value={form.description}
            onChange={(event) => setField('description', event.target.value)}
          />
        </SettingBlock>

        <SettingBlock
          id="bulk-promotional-description"
          label="Promotional description"
          checked={form.includePromotionalDescription}
          onChange={(checked) =>
            setField('includePromotionalDescription', checked)
          }
        >
          <Form.Control
            as="textarea"
            rows={3}
            maxLength={320}
            aria-label="Promotional description text"
            placeholder="Leave blank to clear this on every selected event."
            value={form.promotionalDescription}
            onChange={(event) =>
              setField('promotionalDescription', event.target.value)
            }
          />
        </SettingBlock>

        <SettingBlock
          id="bulk-invoice-email-note"
          label="Invoice email note"
          checked={form.includeInvoiceEmailNote}
          onChange={(checked) => setField('includeInvoiceEmailNote', checked)}
        >
          <Form.Control
            as="textarea"
            rows={4}
            maxLength={1000}
            aria-label="Invoice email note text"
            placeholder="Leave blank to clear this on every selected event. Use {{name}} where the event name should appear."
            value={form.invoiceEmailNote}
            onChange={(event) =>
              setField('invoiceEmailNote', event.target.value)
            }
          />
        </SettingBlock>

        <SettingBlock
          id="bulk-category"
          label="Category"
          checked={form.includeCategory}
          onChange={(checked) => {
            setForm((current) => ({
              ...current,
              includeCategory: checked,
              includeSubcategories: false,
              sub_categories: [],
            }))
            if (checked) loadCategories()
          }}
        >
          <Form.Select
            aria-label="Category"
            value={form.category}
            onChange={(event) => {
              const category = event.target.value
              setForm((current) => ({
                ...current,
                category,
                includeSubcategories: false,
                sub_categories: [],
              }))
            }}
          >
            <option value="">No category</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Form.Select>
          <p className="text-muted small mt-2 mb-0">
            Changing the category clears subcategories on each event unless you
            choose new ones below.
          </p>
        </SettingBlock>

        {availableSubCategories.length > 0 ? (
          <SettingBlock
            id="bulk-subcategories"
            label="Subcategories"
            checked={form.includeSubcategories}
            onChange={(checked) => setField('includeSubcategories', checked)}
          >
            <p className="text-muted small mb-2">
              Replaces subcategories on each event. Leave every box unchecked to
              clear them.
            </p>
            {availableSubCategories.map((subcategory) => (
              <Form.Check
                key={subcategory.id}
                type="checkbox"
                id={`bulk-subcategory-${subcategory.id}`}
                label={subcategory.name}
                checked={form.sub_categories.includes(Number(subcategory.id))}
                onChange={(event) => {
                  const id = Number(subcategory.id)
                  setForm((current) => ({
                    ...current,
                    sub_categories: event.target.checked
                      ? [...current.sub_categories, id]
                      : current.sub_categories.filter(
                          (currentId) => currentId !== id
                        ),
                  }))
                }}
              />
            ))}
          </SettingBlock>
        ) : null}

        <Stack direction="horizontal" gap={2}>
          <Button
            data-testid="bulk-settings-apply"
            disabled={
              processing ||
              eventCount === 0 ||
              labels.length === 0 ||
              Boolean(missingDateMessage)
            }
            onClick={handleApply}
          >
            Review update
          </Button>
          <Button
            variant="outline-light"
            disabled={processing}
            onClick={onClose}
          >
            Cancel
          </Button>
        </Stack>
      </Offcanvas.Body>
    </Offcanvas>
  )
}
